// Monaco-like config editor with simple syntax highlighting (YAML / JSON / .env).

import { useState, useRef, useEffect, useMemo } from 'react';
import { I } from './icons';

type Lang = 'yaml' | 'json' | 'env';

function highlightLine(line: string, lang: Lang): string {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  let l = esc(line);
  if (lang === 'yaml') {
    l = l.replace(/(#.*)$/, '<span class="cm-comment">$1</span>');
    l = l.replace(/^(\s*-?\s*)([A-Za-z_][\w.-]*)(\s*:)/,
      '$1<span class="cm-key">$2</span>$3');
    l = l.replace(/(&quot;[^&]*?&quot;|'[^']*?')/g, '<span class="cm-str">$1</span>');
    l = l.replace(/^(\s*)(-)(\s)/, '$1<span class="cm-dash">$2</span>$3');
    l = l.replace(/(:\s)(\d+\.?\d*)\b/g, '$1<span class="cm-num">$2</span>');
    l = l.replace(/(:\s)(true|false|null)\b/g, '$1<span class="cm-bool">$2</span>');
  } else if (lang === 'json') {
    l = l.replace(/(&quot;[^&]*?&quot;)(\s*:)/g, '<span class="cm-key">$1</span>$2');
    l = l.replace(/(:\s*)(&quot;[^&]*?&quot;)/g, '$1<span class="cm-str">$2</span>');
    l = l.replace(/(:\s*)(-?\d+\.?\d*)/g, '$1<span class="cm-num">$2</span>');
    l = l.replace(/(:\s*)(true|false|null)\b/g, '$1<span class="cm-bool">$2</span>');
  } else if (lang === 'env') {
    l = l.replace(/(#.*)$/, '<span class="cm-comment">$1</span>');
    l = l.replace(/^([A-Z_][A-Z0-9_]*)(=)/, '<span class="cm-key">$1</span>$2');
  }
  return l || '&nbsp;';
}

function detectLang(filename: string): Lang {
  if (filename.endsWith('.yaml') || filename.endsWith('.yml')) return 'yaml';
  if (filename.endsWith('.json')) return 'json';
  if (filename.endsWith('.env') || filename.startsWith('.env')) return 'env';
  return 'yaml';
}

export function ConfigEditor({
  files, pvcPath = '/data/config', onSave, readOnly,
}: {
  files: Record<string, string>;
  pvcPath?: string;
  onSave?: (name: string, content: string) => void;
  readOnly?: boolean;
}) {
  const filenames = Object.keys(files);
  const [openTabs, setOpenTabs] = useState<string[]>([filenames[0]].filter(Boolean));
  const [active, setActive] = useState<string>(filenames[0]);
  const [buffers, setBuffers] = useState<Record<string, string>>(files);
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setBuffers(files);
    setDirty({});
    // Re-sync tabs when the file set changes (e.g. switching services), else
    // `active` can point at a filename absent from the new files → empty editor.
    const names = Object.keys(files);
    setActive((cur) => (names.includes(cur) ? cur : names[0]));
    setOpenTabs((tabs) => {
      const kept = tabs.filter((t) => names.includes(t));
      return kept.length ? kept : [names[0]].filter(Boolean);
    });
  }, [files]);

  const openFile = (name: string) => {
    if (!openTabs.includes(name)) setOpenTabs([...openTabs, name]);
    setActive(name);
  };
  const closeTab = (e: React.MouseEvent, name: string) => {
    e.stopPropagation();
    const next = openTabs.filter((t) => t !== name);
    setOpenTabs(next);
    if (active === name) setActive(next[next.length - 1] || filenames[0]);
  };

  const onEdit = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value;
    setBuffers((b) => ({ ...b, [active]: v }));
    setDirty((d) => ({ ...d, [active]: v !== files[active] }));
  };

  const lang = detectLang(active || '');
  const content = buffers[active] || '';
  const lines = content.split('\n');

  const saveAll = () => {
    Object.keys(dirty).forEach((k) => {
      if (dirty[k]) onSave?.(k, buffers[k]);
    });
    setDirty({});
  };

  const anyDirty = Object.values(dirty).some(Boolean);

  const highlightedHtml = useMemo(() => {
    return lines.map((l) => `<div>${highlightLine(l, lang)}</div>`).join('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, lang]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div className="row" style={{ gap: 8 }}>
          <I.Folder size={14}/>
          <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{pvcPath}</span>
          <span className="badge badge-violet" style={{ marginLeft: 6 }}>
            <I.Layers size={11}/>
            PVC
          </span>
        </div>
        <div className="row" style={{ gap: 6 }}>
          {readOnly ? (
            <span className="badge" style={{ fontSize: 11 }}>read-only</span>
          ) : (
            <>
              {anyDirty && (
                <span style={{ fontSize: 11.5, color: 'var(--amber)' }}>● unsaved changes</span>
              )}
              <button className="btn btn-sm" disabled={!anyDirty} onClick={() => { setBuffers(files); setDirty({}); }}>
                Revert
              </button>
              <button className="btn btn-sm btn-primary" disabled={!anyDirty} onClick={saveAll}>
                <I.Save size={13}/>
                Save &amp; sync
              </button>
            </>
          )}
        </div>
      </div>

      <div className="editor-wrap">
        <div className="editor-side">
          <div className="editor-side-head">
            <span>Files</span>
            <span className="mono">{filenames.length}</span>
          </div>
          <div className="editor-tree">
            <div className="tree-item dir"><I.FolderO size={13}/>{pvcPath.replace(/^\//, '')}</div>
            {filenames.map((name) => (
              <div key={name}
                   className={'tree-item' + (active === name ? ' active' : '') + (dirty[name] ? ' modified' : '')}
                   onClick={() => openFile(name)}>
                <I.File size={13}/>
                <span>{name}</span>
              </div>
            ))}
          </div>
          <div style={{ padding: 10, borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--text-3)' }}>
            <div style={{ marginBottom: 4 }}>Mounted at:</div>
            <div className="mono" style={{ color: 'var(--text-2)', wordBreak: 'break-all' }}>{pvcPath}</div>
          </div>
        </div>
        <div className="editor-main">
          <div className="editor-tabs">
            {openTabs.map((name) => (
              <div key={name}
                   className={'editor-tab' + (active === name ? ' active' : '')}
                   onClick={() => setActive(name)}>
                <I.File size={11}/>
                <span>{name}{dirty[name] && ' •'}</span>
                <span className="close" onClick={(e) => closeTab(e, name)} style={{ marginLeft: 4, display: 'inline-flex' }}>
                  <I.X size={11}/>
                </span>
              </div>
            ))}
          </div>
          <div className="editor-pane">
            <div className="gutter">
              {lines.map((_, i) => <div key={i}>{i + 1}</div>)}
            </div>
            <div style={{ position: 'relative', overflow: 'auto' }}>
              <div className="editor-code"
                   style={{ position: 'absolute', inset: 0, pointerEvents: 'none', whiteSpace: 'pre' }}
                   dangerouslySetInnerHTML={{ __html: highlightedHtml }}/>
              <textarea ref={taRef}
                        value={content}
                        onChange={onEdit}
                        readOnly={readOnly}
                        spellCheck={false}
                        className="editor-code"
                        style={{
                          position: 'absolute', inset: 0,
                          background: 'transparent',
                          color: 'transparent',
                          caretColor: 'var(--accent)',
                          border: 0, outline: 0,
                          resize: 'none',
                          whiteSpace: 'pre',
                          width: '100%', height: '100%',
                        }}/>
            </div>
          </div>
          <div className="editor-foot">
            <span>{lang.toUpperCase()}</span>
            <span>UTF-8</span>
            <span>LF</span>
            <span className="grow"></span>
            <span>Ln {lines.length}</span>
            <span>{content.length} chars</span>
            {dirty[active] && <span style={{ color: 'var(--amber)' }}>● modified</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
